import type { ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, X, Check } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";

/**
 * Reusable "dry-run → preview → apply" modal for idempotent admin actions that
 * expose a `?dry=true|false` endpoint (merge duplicates, auto-organize albums).
 * Fetches the dry-run on open, renders a caller-supplied preview, and applies on
 * confirm. Keeps the two flows from re-implementing the same scaffold.
 */
export default function DryRunActionModal<T>({
  title,
  endpoint,
  dryQueryKey,
  applyLabel,
  canApply,
  renderPreview,
  onClose,
  onDone,
}: {
  title: string;
  endpoint: string;
  dryQueryKey: string[];
  applyLabel: (data: T | undefined) => string;
  canApply: (data: T | undefined) => boolean;
  renderPreview: (data: T | undefined, isLoading: boolean) => ReactNode;
  onClose: () => void;
  onDone: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: dryQueryKey,
    queryFn: () => api.post<ApiResponse<T>>(`${endpoint}?dry=true`, {}),
    refetchOnWindowFocus: false,
  });
  const result = data?.data;

  const applyMutation = useMutation({
    mutationFn: () => api.post<ApiResponse<T>>(`${endpoint}?dry=false`, {}),
    onSuccess: onDone,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">{renderPreview(result, isLoading)}</div>

        <div className="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
          <button onClick={onClose} className="btn-ghost text-sm">
            Cancel
          </button>
          <button
            onClick={() => applyMutation.mutate()}
            disabled={isLoading || !canApply(result) || applyMutation.isPending}
            className="btn-primary text-sm"
          >
            {applyMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {applyLabel(result)}
          </button>
        </div>
      </div>
    </div>
  );
}
